import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Printer, FileBox, CheckCircle, X } from 'lucide-react';
import type { UnifiedOrder } from './types';
import * as actions from './actions';
import { Can, useCanAny } from '../../lib/rbac';

interface BulkActionBarProps {
  selectedOrders: UnifiedOrder[];
  sellerId: number;
  onClear: () => void;
  onSuccess: () => void;
}

export function BulkActionBar({ selectedOrders, sellerId, onClear, onSuccess }: BulkActionBarProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<string | null>(null);
  // Sin ninguna acción masiva permitida, la barra entera no aparece.
  const canBulk = useCanAny(['orders.operate', 'orders.buy']);

  if (selectedOrders.length === 0 || !canBulk) return null;

  const handleMergeLabels = async () => {
    const shipmentIds = selectedOrders.flatMap(o =>
      o.shipments.map(s => s.ml_shipment_id)
    );
    if (shipmentIds.length === 0) {
      alert(t('orders.bulk.no_shipments', 'No hay envios en la seleccion'));
      return;
    }
    setLoading('merge');
    try {
      const res = await actions.mergeLabels(sellerId, shipmentIds) as any;
      if (!res.success && res.data?.count === 0 && res.data?.generated === 0) {
        alert(t('orders.bulk.merge_dedup', 'Ya se hizo un merge recientemente. Espera unos minutos o usa force.'));
      } else if (res.data?.count === 0 && res.data?.generated === 0) {
        alert(t('orders.bulk.no_labels', 'No hay labels para mergear. Los shipments seleccionados no tienen labels generados.'));
      } else {
        const skipped = res.data?.skipped as Array<{ shipmentId: number; orderId: number; reason: string }> | undefined;
        const msg = [
          res.data?.generated > 0 ? `${res.data.generated} labels generados` : null,
          res.data?.count > 0 ? `${res.data.count} labels mergeados` : null,
          skipped?.length ? `⚠️ ${skipped.length} labels sin ML shipping label (shipments: ${skipped.map(s => s.shipmentId).join(', ')})` : null,
          res.data?.url ? `PDF: ${res.data.url}` : null,
        ].filter(Boolean).join('\n');
        alert(msg || 'Merge completado');
        if (res.data?.url) {
          window.open(res.data.url, '_blank');
        }
        onSuccess();
        onClear();
      }
    } catch (err: any) {
      alert(err.message || 'Error merging labels');
    } finally {
      setLoading(null);
    }
  };

  const handleCreatePO = async () => {
    const purchaseIds = selectedOrders.flatMap(o =>
      o.purchases.filter(p => (p.amazon_status === 'pending_approval' || p.amazon_status === 'safeguard_hold') && !p.stock_po_id && !p.po_id).map(p => p.id)
    );
    if (purchaseIds.length === 0) {
      alert(t('orders.bulk.no_purchases', 'No hay compras elegibles para PO en la seleccion'));
      return;
    }
    setLoading('po');
    try {
      const res = await actions.createPO(sellerId, purchaseIds) as any;
      if (res.success) {
        alert(`PO creada: ${res.data?.po_number || 'OK'} (${purchaseIds.length} items)`);
        onSuccess();
        onClear();
      } else {
        alert(res.error || 'Error creating PO');
      }
    } catch (err: any) {
      alert(err.message || 'Error creating PO');
    } finally {
      setLoading(null);
    }
  };

  const handleBulkApprove = async () => {
    const purchaseIds = selectedOrders
      .filter(o => o.status !== 'cancelled')
      .flatMap(o =>
        o.purchases
          .filter(p => p.amazon_status === 'pending_approval' || p.amazon_status === 'safeguard_hold')
          .map(p => p.id)
      );
    if (purchaseIds.length === 0) {
      alert(t('orders.bulk.no_pending', 'No hay compras pendientes de aprobacion en la seleccion'));
      return;
    }
    setLoading('approve');
    try {
      const results = await actions.bulkApprove(purchaseIds);
      if (results.failed > 0) {
        alert(t('orders.bulk.partial_approve', '{{ok}} aprobadas, {{failed}} fallaron', {
          ok: results.ok,
          failed: results.failed,
        }));
      }
      onSuccess();
      onClear();
    } catch (err: any) {
      alert(err.message || 'Error approving purchases');
    } finally {
      setLoading(null);
    }
  };

  const isLoading = loading !== null;

  return (
    <div
      className="fixed bottom-14 lg:bottom-0 left-0 right-0 z-30 border-t border-[var(--color-border)] bg-[var(--color-surface)] shadow-[0_-4px_12px_rgba(0,0,0,0.1)] animate-slide-up"
    >
      <div className="max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <span className="text-sm font-medium text-[var(--color-text)]">
          {t('orders.bulk.selected', '{{count}} ordenes seleccionadas', { count: selectedOrders.length })}
        </span>

        <div className="flex items-center gap-2">
          <Can cap="orders.operate">
            <button
              onClick={handleMergeLabels}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {loading === 'merge' ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
              {t('orders.bulk.merge_labels', 'Merge Labels')}
            </button>
          </Can>

          {/* Crear una orden de compra sin ver costos cargaría costo cero: exige también ver dinero. */}
          <Can cap="orders.operate">
            <Can cap="global.view_money">
              <button
                onClick={handleCreatePO}
                disabled={isLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {loading === 'po' ? <Loader2 size={14} className="animate-spin" /> : <FileBox size={14} />}
                {t('orders.bulk.create_po', 'Crear PO')}
              </button>
            </Can>
          </Can>

          <Can cap="orders.buy">
            <button
              onClick={handleBulkApprove}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              {loading === 'approve' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              {t('orders.bulk.approve_all', 'Aprobar Todos')}
            </button>
          </Can>

          <button
            onClick={onClear}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50 transition-colors"
          >
            <X size={14} />
            {t('orders.bulk.deselect', 'Deseleccionar')}
          </button>
        </div>
      </div>
    </div>
  );
}
